import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createFonte, fetchFontes, type BiSource } from '../api/bridge'

const STORAGE_KEY = 'fiesta-bi:active-source'

type BiSourceContextValue = {
  sources: BiSource[]
  activeSource: BiSource | null
  activeSourceId: string
  isFiestaActive: boolean
  loading: boolean
  error: string | null
  setActiveSourceId: (id: string) => void
  refreshSources: () => void
  addSource: (payload: {
    name: string
    slug?: string
    role?: string
    logo_url?: string
    origin_product_id?: string
    actor?: string
  }) => Promise<{
    fonte: BiSource
    bind_s1?: {
      ok?: boolean
      error?: string
      connection_id?: string
      authz_status?: string
      smoke?: { ok?: boolean; pedidos_na_pagina?: number }
      note?: string
    } | null
  }>
  replaceSource: (fonte: BiSource) => void
}

const BiSourceContext = createContext<BiSourceContextValue | null>(null)

function readStoredId(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'fiesta'
  } catch {
    return 'fiesta'
  }
}

export function BiSourceProvider({ children }: { children: ReactNode }) {
  const [sources, setSources] = useState<BiSource[]>([])
  const [activeSourceId, setActiveSourceIdState] = useState(readStoredId)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refreshSources = useCallback(() => setTick((t) => t + 1), [])

  const setActiveSourceId = useCallback((id: string) => {
    const next = (id || 'fiesta').trim().toLowerCase()
    setActiveSourceIdState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchFontes(false)
      .then((res) => {
        if (cancelled) return
        const list = res.fontes || []
        setSources(list)
        const visible = list.filter((s) => s.visible_in_bi !== false)
        const ids = new Set(visible.map((s) => s.id))
        const fallback =
          visible.find((s) => s.default_in_bi)?.id ||
          visible.find((s) => s.id === 'fiesta')?.id ||
          visible[0]?.id ||
          'fiesta'
        setActiveSourceIdState((current) => {
          if (ids.has(current)) return current
          try {
            localStorage.setItem(STORAGE_KEY, fallback)
          } catch {
            /* ignore */
          }
          return fallback
        })
        fetchFontes(true)
          .then((counted) => {
            if (cancelled) return
            setSources(counted.fontes || list)
          })
          .catch(() => {
            /* counts são opcionais */
          })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSources([
            {
              id: 'fiesta',
              name: 'Fiesta',
              db_name: 'ecommerce_financial',
              builtin: true,
              status: 'active',
            },
          ])
          setError(err instanceof Error ? err.message : 'Falha ao carregar fontes')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tick])

  const replaceSource = useCallback((fonte: BiSource) => {
    setSources((prev) => prev.map((s) => (s.id === fonte.id ? { ...s, ...fonte } : s)))
  }, [])

  const addSource = useCallback(
    async (payload: {
      name: string
      slug?: string
      role?: string
      logo_url?: string
      origin_product_id?: string
      actor?: string
    }) => {
      const res = await createFonte(payload)
      if (!res.ok || !res.fonte) {
        throw new Error(res.error || 'Não foi possível criar a fonte')
      }
      refreshSources()
      setActiveSourceId(res.fonte.id)
      return { fonte: res.fonte, bind_s1: res.bind_s1 ?? null }
    },
    [refreshSources, setActiveSourceId],
  )

  const activeSource = useMemo(
    () => sources.find((s) => s.id === activeSourceId) || sources[0] || null,
    [sources, activeSourceId],
  )

  const value = useMemo(
    () => ({
      sources,
      activeSource,
      activeSourceId: activeSource?.id || 'fiesta',
      isFiestaActive: (activeSource?.id || 'fiesta') === 'fiesta',
      loading,
      error,
      setActiveSourceId,
      refreshSources,
      addSource,
      replaceSource,
    }),
    [
      sources,
      activeSource,
      loading,
      error,
      setActiveSourceId,
      refreshSources,
      addSource,
      replaceSource,
    ],
  )

  return <BiSourceContext.Provider value={value}>{children}</BiSourceContext.Provider>
}

export function useBiSource() {
  const ctx = useContext(BiSourceContext)
  if (!ctx) throw new Error('useBiSource fora de BiSourceProvider')
  return ctx
}
